import { CircleHelp } from "lucide-react";
import { Checkbox, Compact, FormItem, Input, NumberField, Select, TextArea, Tooltip } from "~/components/ui";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { compactFormItemStyle, type SchemaForm } from "~/utils/schema-admin";
import { CHUNKING_STRATEGY_VALUES, PARSER_OPTIONS } from "~/utils/schema-form-utils";
import { HelpLabel } from "./help-label";

interface SchemaGeneralSectionProps {
  isEdit: boolean;
}

/** Schema identity, parsing and chunking settings block of the edit modal. */
export function SchemaGeneralSection({ isEdit }: SchemaGeneralSectionProps) {
  const { t } = useTranslation("admin");
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<SchemaForm>();

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          required
          label={<HelpLabel label={t("schemaAdmin.columns.schemaId")} helpKey="schemaAdmin.help.schemaId" />}
          validateStatus={errors.schema_id ? "error" : ""}
          help={errors.schema_id?.message}
          className={compactFormItemStyle}
        >
          <Controller
            name="schema_id"
            control={control}
            render={({ field }) => (
              <Input {...field} disabled={isEdit} placeholder={t("schemaAdmin.placeholders.schemaId")} />
            )}
          />
        </FormItem>

        <FormItem
          required
          label={<HelpLabel label={t("schemaAdmin.columns.docType")} helpKey="schemaAdmin.help.docType" />}
          validateStatus={errors.doc_type ? "error" : ""}
          help={errors.doc_type?.message}
          className={compactFormItemStyle}
        >
          {/* PATCH does not accept doc_type (backend rejects it), so editing
              it would be silently ignored — lock it like schema_id */}
          <Controller
            name="doc_type"
            control={control}
            render={({ field }) => (
              <Input {...field} disabled={isEdit} placeholder={t("schemaAdmin.placeholders.docType")} />
            )}
          />
        </FormItem>
      </div>

      <FormItem
        required
        label={<HelpLabel label={t("schemaAdmin.columns.description")} helpKey="schemaAdmin.help.description" />}
        validateStatus={errors.description ? "error" : ""}
        help={errors.description?.message}
        className={compactFormItemStyle}
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextArea {...field} rows={2} placeholder={t("schemaAdmin.placeholders.description")} />
          )}
        />
      </FormItem>

      <FormItem
        label={<HelpLabel label={t("schemaAdmin.columns.promptHint")} helpKey="schemaAdmin.help.promptHint" />}
        className={compactFormItemStyle}
      >
        <Controller
          name="prompt_hint"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              value={field.value ?? ""}
              rows={2}
              placeholder={t("schemaAdmin.placeholders.promptHint")}
            />
          )}
        />
      </FormItem>

      <div className="flex gap-4 flex-wrap">
        <FormItem
          label={<HelpLabel label={t("schemaAdmin.columns.version")} helpKey="schemaAdmin.help.version" />}
          validateStatus={errors.version ? "error" : ""}
          className={compactFormItemStyle}
        >
          <Controller
            name="version"
            control={control}
            render={({ field }) => <NumberField {...field} min={1} className="w-24" />}
          />
        </FormItem>

        <FormItem
          label={<HelpLabel label={t("schemaAdmin.columns.parser")} helpKey="schemaAdmin.help.parser" />}
          className={compactFormItemStyle}
        >
          <Controller
            name="parser"
            control={control}
            render={({ field }) => (
              <Select {...field} value={field.value ?? ""} options={PARSER_OPTIONS} style={{ width: 180 }} />
            )}
          />
        </FormItem>

        <FormItem
          label={<HelpLabel label={t("schemaAdmin.columns.chunking")} helpKey="schemaAdmin.help.chunking" />}
          className={compactFormItemStyle}
          group
        >
          <Compact>
            <Controller
              name="chunking.strategy"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ""}
                  options={CHUNKING_STRATEGY_VALUES.map((v) => ({
                    value: v,
                    label: t(`schemaAdmin.chunking.${v || "default"}`),
                  }))}
                  style={{ width: 150 }}
                  placeholder={t("schemaAdmin.placeholders.strategy")}
                  aria-label={t("schemaAdmin.placeholders.strategy")}
                />
              )}
            />
            <Controller
              name="chunking.min_chunk_chars"
              control={control}
              render={({ field }) => (
                <NumberField
                  {...field}
                  min={0}
                  placeholder={t("schemaAdmin.placeholders.minChars")}
                  aria-label={t("schemaAdmin.placeholders.minChars")}
                  className="w-[100px]"
                />
              )}
            />
            {watch("chunking.strategy") === "numbered_items" && (
              <Tooltip title={t("schemaAdmin.help.itemsPerChunk")}>
                <span className="inline-flex">
                  <Controller
                    name="chunking.items_per_chunk"
                    control={control}
                    render={({ field }) => (
                      <NumberField
                        {...field}
                        min={1}
                        placeholder={t("schemaAdmin.chunking.itemsPerChunkPlaceholder")}
                        aria-label={t("schemaAdmin.chunking.itemsPerChunkPlaceholder")}
                        className="w-[110px]"
                      />
                    )}
                  />
                </span>
              </Tooltip>
            )}
          </Compact>
        </FormItem>

        <FormItem className={compactFormItemStyle} label=" ">
          <Tooltip title={t("schemaAdmin.help.extractImages")}>
            <span className="inline-flex">
              <Controller
                name="extract_page_images"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    {t("schemaAdmin.columns.extractImages")} <CircleHelp size={14} className="text-noa-muted" />
                  </Checkbox>
                )}
              />
            </span>
          </Tooltip>
        </FormItem>
      </div>
    </>
  );
}
